import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { DownloadSimple, EnvelopeSimple } from "@phosphor-icons/react";
import { startDownload, backendDownloadUrl, downloadFromBackend } from "../../lib/download";
import { displayProduct } from "../../lib/products";
import { computeInvoiceTaxBreakdown } from "../../lib/tax";
import { InvoiceSummaryTotals } from "../../components/InvoiceSummaryTotals";
import EmailSendModal from "../../components/EmailSendModal";
import { formatCard } from "../../lib/cardDisplay";

function invoicePdfHref(invoiceId) {
  return backendDownloadUrl(`/invoices/${invoiceId}/pdf`);
}

const downloadInvoicePdf = async (invoiceId) => {
  try {
    await downloadFromBackend(`/invoices/${invoiceId}/pdf`, {});
  } catch (_) {}
};

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showPay, setShowPay] = useState(false);
  const [pay, setPay] = useState({ amount: 0, method: "bank_transfer", reference: "" });
  const [showEmail, setShowEmail] = useState(false);
  const [emailDefaults, setEmailDefaults] = useState(null);
  const openEmail = async () => {
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/email-defaults`);
      setEmailDefaults(data);
      setShowEmail(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not prepare the invoice email");
    }
  };
  const [emailHistory, setEmailHistory] = useState([]);
  // Admin-configurable low-margin threshold (% — e.g. 3 = 3%). Falls back
  // to 3 if the settings endpoint is slow or missing — same default the
  // backend stores in `routes/settings.py::_DEFAULTS`.
  const [lowMarginPct, setLowMarginPct] = useState(3);

  const loadEmailHistory = async () => {
    try {
      const { data } = await api.get(`/email/events`, { params: { related_id: invoiceId, kind: "invoice", limit: 10 } });
      setEmailHistory(data || []);
    } catch (_) { /* email history is optional */ }
  };

  const load = async () => {
    const [d, p] = await Promise.all([
      api.get(`/invoices/${invoiceId}`),
      api.get(`/invoices/${invoiceId}/payments`),
    ]);
    setData(d.data); setPayments(p.data);
  };
  useEffect(() => { load(); loadEmailHistory(); /* eslint-disable-next-line */ }, [invoiceId]);
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (typeof data?.low_margin_threshold_pct === "number") {
        setLowMarginPct(data.low_margin_threshold_pct);
      }
    }).catch(() => { /* fallback to default 3% */ });
  }, []);

  const recordPayment = async () => {
    try {
      await api.post(`/invoices/${invoiceId}/payments`, pay);
      toast.success("Payment recorded"); setShowPay(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="p-8 font-mono">LOADING…</div>;
  const { invoice, customer, line_items } = data;

  return (
    <SectionPad>
      <div className="mb-4"><Link to="/invoices" className="overline hover:underline">← INVOICES</Link></div>
      <PageHeader
        title={invoice.invoice_number}
        subtitle={`INVOICE / ${customer?.company_name}`}
        testId="invoice-detail-header"
        actions={
          <>
            <button className="gf-btn gf-btn-outline" onClick={openEmail} data-testid="email-invoice-button">
              <EnvelopeSimple size={14}/> Email invoice
            </button>
            <button className="gf-btn gf-btn-outline" onClick={() => setShowPay(true)} data-testid="record-payment-button">Record payment</button>
            <a className="gf-btn gf-btn-primary" href="#" onClick={(e) => { e.preventDefault(); downloadInvoicePdf(invoiceId); }}
               data-testid="download-pdf-button"><DownloadSimple size={14}/> PDF</a>
          </>
        }
      />

      {(() => {
        // GP-this-invoice tile (admin-only). The customer's API response
        // strips `gross_profit` so this block silently degrades to nothing
        // if a customer ever lands here. Margin% denominator is the
        // pre-tax subtotal — gives a stable cross-invoice comparison.
        const gpTotal = (line_items || []).reduce((s, t) => s + Number(t.gross_profit || 0), 0);
        const subtotal = (line_items || []).reduce((s, t) => s + Number(t.customer_amount || 0), 0);
        const marginPct = subtotal > 0 ? (gpTotal / subtotal) * 100 : 0;
        // Light visual cue: low-margin invoices in red, normal in green.
        // The threshold is admin-configurable via /api/settings — operator
        // can dial it up if the business adds a higher-margin service line.
        const isLow = marginPct < lowMarginPct && subtotal > 0;
        const gpColor = isLow ? "#dc2626" : "#16a34a";
        return (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="gf-card gf-card-body"><div className="overline">ISSUED</div><div className="font-mono mt-1">{invoice.issue_date}</div></div>
            <div className="gf-card gf-card-body"><div className="overline">DUE</div><div className="font-mono mt-1">{invoice.due_date}</div></div>
            <div className="gf-card gf-card-body"><div className="overline">PERIOD</div><div className="font-mono text-xs mt-1">{invoice.period_start} → {invoice.period_end}</div></div>
            <div className="gf-card gf-card-body"><div className="overline">STATUS</div><div className="mt-1"><Badge variant={invoice.status === "paid" ? "success" : invoice.status === "overdue" ? "error" : "info"} data-testid="invoice-status-badge">{invoice.status}</Badge></div></div>
            <div className="gf-card gf-card-body md:col-span-4" data-testid="gp-this-invoice-tile" style={{ borderLeft: `4px solid ${gpColor}` }}>
              <div className="flex flex-wrap items-baseline justify-between gap-4">
                <div>
                  <div className="overline">GP THIS INVOICE · ADMIN ONLY</div>
                  <div className="font-mono font-semibold mt-1 text-xl" style={{ color: gpColor }} data-testid="gp-this-invoice-value">
                    ${gpTotal.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="overline">MARGIN %</div>
                  <div className="font-mono font-semibold mt-1 text-xl" style={{ color: gpColor }} data-testid="gp-this-invoice-margin-pct">
                    {marginPct.toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div className="overline">SUBTOTAL (PRE-TAX)</div>
                  <div className="font-mono font-semibold mt-1 text-xl">${subtotal.toFixed(2)}</div>
                </div>
                <div className="text-xs text-zinc-500 max-w-[280px]">
                  {isLow
                    ? `⚠ Margin under ${lowMarginPct}% — check for missing pricing rules or manual overrides before sending.`
                    : "Computed from per-line gross_profit. Never shown to customers."}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="gf-card mb-4">
        <div className="gf-card-header"><h3>Line items ({line_items.length})</h3></div>
        <div className="overflow-x-auto">
          {(() => {
            // Compute per-row tax + per-province totals in one pass — same
            // logic the backend uses for the PDF & email attachment, so the
            // numbers reconcile across surfaces.
            const { rows, totals, byProv, isUsa } = computeInvoiceTaxBreakdown(line_items);
            const provinces = Object.keys(byProv).sort();
            return (
              <>
                <table className="gf-table">
                  <thead>
                    <tr>
                      <th>DATE</th><th>CARD</th><th>STATION</th><th>CITY</th><th>PROV</th><th>PRODUCT</th>
                      <th className="text-right" data-testid="invoice-col-qty">QTY ({isUsa ? "GAL" : "L"})</th><th className="text-right" data-testid="invoice-col-price">{isUsa ? "PRICE/GAL" : "PRICE/LTR"}</th>
                      <th className="text-right">AMOUNT</th>
                      <th className="text-right">GST/HST</th><th className="text-right">PST</th>
                      <th className="text-right">FINAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr key={t.transaction_id}>
                        <td className="font-mono text-xs">{t.tran_date}</td>
                        <td className="font-mono">{formatCard(t.card_number, t.network, t.card_last4)}</td>
                        <td className="text-xs">{t.station_name}</td>
                        <td className="text-xs">{t.city}</td>
                        <td className="font-mono text-xs">{(t.province || t.state || "").toUpperCase()}</td>
                        <td className="font-mono text-xs">{displayProduct(t.product, t.network)}</td>
                        <td className="gf-num">{t.qty?.toFixed(2)}</td>
                        <td className="gf-num">{t.customer_cpl?.toFixed(4)}</td>
                        <td className="gf-num">${t.customer_amount?.toFixed(2)}</td>
                        <td className="gf-num" data-testid={`invoice-line-gst-${t.transaction_id}`}>${t.gst.toFixed(2)}</td>
                        <td className="gf-num" data-testid={`invoice-line-pst-${t.transaction_id}`}>${t.pst.toFixed(2)}</td>
                        <td className="gf-num">${t.finalAmount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#F4F4F5" }}>
                      <td colSpan={6} className="text-right font-mono font-bold">TOTAL</td>
                      <td className="gf-num font-bold">{totals.qty.toFixed(2)}</td>
                      <td></td>
                      <td className="gf-num font-bold">${totals.amount.toFixed(2)}</td>
                      <td className="gf-num font-bold">${totals.gst.toFixed(2)}</td>
                      <td className="gf-num font-bold">${totals.pst.toFixed(2)}</td>
                      <td className="gf-num font-bold">${totals.finalAmount.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>

                {provinces.length > 0 && (
                  <div className="p-4 border-t border-zinc-200">
                    <div className="overline mb-2">TAX BREAKDOWN BY PROVINCE</div>
                    <table className="gf-table" style={{ maxWidth: 480 }}>
                      <thead>
                        <tr>
                          <th>PROVINCE</th>
                          <th className="text-right">GST/HST</th>
                          <th className="text-right">PST</th>
                          <th className="text-right">SUBTOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {provinces.map((p) => (
                          <tr key={p}>
                            <td className="font-mono">{p}</td>
                            <td className="gf-num">${byProv[p].gst.toFixed(2)}</td>
                            <td className="gf-num">${byProv[p].pst.toFixed(2)}</td>
                            <td className="gf-num">${(byProv[p].gst + byProv[p].pst).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: "#F4F4F5" }}>
                          <td className="font-mono font-bold">TOTAL</td>
                          <td className="gf-num font-bold">${totals.gst.toFixed(2)}</td>
                          <td className="gf-num font-bold">${totals.pst.toFixed(2)}</td>
                          <td className="gf-num font-bold">${(totals.gst + totals.pst).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <div className="p-4 border-t border-zinc-200 flex justify-end">
                  <InvoiceSummaryTotals invoice={invoice} totals={totals} isUsa={isUsa} />
                </div>
              </>
            );
          })()}
        </div>
      </div>

      <div className="gf-card">
        <div className="gf-card-header"><h3>Payments</h3><div className="overline">PAID ${(invoice.paid_amount || 0).toFixed(2)} / ${invoice.total.toFixed(2)}</div></div>
        <div className="overflow-x-auto">
          <table className="gf-table">
            <thead><tr><th>DATE</th><th>METHOD</th><th>REF</th><th className="text-right">AMOUNT</th></tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.payment_id}>
                  <td className="font-mono text-xs">{p.paid_date}</td>
                  <td className="text-xs">{p.method}</td>
                  <td className="font-mono text-xs">{p.reference || "—"}</td>
                  <td className="gf-num">${p.amount.toFixed(2)}</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-400 py-6">No payments recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showPay && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPay(false)}>
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Record payment</h3></div>
            <div className="gf-card-body space-y-3">
              <div><div className="overline mb-1">Amount</div><input type="number" step="0.01" className="gf-input" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: parseFloat(e.target.value) || 0 })} data-testid="payment-amount-input" /></div>
              <div><div className="overline mb-1">Method</div>
                <select className="gf-input" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                  <option value="bank_transfer">Bank transfer</option><option value="cheque">Cheque</option><option value="credit_card">Credit card</option><option value="other">Other</option>
                </select>
              </div>
              <div><div className="overline mb-1">Reference</div><input className="gf-input" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowPay(false)}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={recordPayment} data-testid="save-payment-button">Record</button>
            </div>
          </div>
        </div>
      )}
      {showEmail && (
        <EmailSendModal
          kind="invoice"
          customer={customer}
          recipient={customer?.billing_email || customer?.email}
          defaultSubject={emailDefaults?.subject || ""}
          defaultBody={emailDefaults?.body || ""}
          onSend={async (overrides) => {
            // Immediate route only: one attempt, server-side manual flag,
            // official invoice PDF. No background retries.
            const { data } = await api.post(`/invoices/${invoiceId}/email`, {
              subject_override: overrides.subject || null,
              body_override: overrides.body || null,
              cc_operator: overrides.cc,
            });
            return data;
          }}
          onClose={() => { setShowEmail(false); loadEmailHistory(); load(); }}
          history={emailHistory}
        />
      )}
    </SectionPad>
  );
}
